import { BetaSchemaForm } from "@ant-design/pro-components";
import { Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNodegroupConfig } from "@/hooks/config/useNodegroupConfig";
import { useHadoopConfig } from "@/hooks/config/useHadoopConfig";
import { useMySQLConfig } from "@/hooks/config/useMySQLConfig";
import { useVIAConfig } from "@/hooks/config/useVIAConfig";
import { useDruidConfig } from "@/hooks/config/useDruidConfig";
import { useGiteaConfig } from "@/hooks/config/useGiteaConfig";
import { useNeo4jConfig } from "@/hooks/config/useNeo4jConfig";
import { useOne, useUpdate, useInvalidate } from "@refinedev/core";
import { useResourceNavigate } from "@/hooks/useResourceNavigate";
import { deriveUIDefaultsFromServerFormat } from "../../config/ServerToClientConverter";
import { appendDefaultValuesToColumns } from "../../config/ServerToClientConverter";
import { genUpdateProfileForServer } from "../../config/ClientToServerConverter";
import { UpdateWrapper } from "@/layouts/UpdateWrapper";

import {
  CHANGE_NODEGROUP,
  CHANGE_HADOOP,
  CHANGE_MYSQL,
  CHANGE_VIA,
  CHANGE_DRUID,
  ENABLE_DRUID,
  CHANGE_GITEA,
  ENABLE_GITEA,
  CHANGE_NEO4J,
  ENABLE_NEO4J
} from "@/resources-def";

export const UpdateInstanceProfile = ({ id, action, goto }) => {
  const [defaultValues, setDefaultValues] = useState({});
  const { data, isLoading, isError } = useOne({
    resource: "instances",
    id,
    meta: { action },
  });

  useEffect(() => {
    if (isLoading) return;

    if (isError || data?.data?.status === "error") {
      // notification.error({
      //   message: "Error",
      //   description: "Fail to get instance profile",
      // });
      return;
    }
    const initialValue = deriveUIDefaultsFromServerFormat(data);
    setDefaultValues(initialValue);
  }, [data, isLoading, isError]);

  return isError ? (
    <div>Failed to get instance profile.</div>
  ) : (
    <GetProfileDef
      id={id}
      isLoading={isLoading}
      action={action}
      defaultValues={defaultValues}
      goto={goto}
      data={data}
    ></GetProfileDef>
  );
};

export const GetProfileDef = ({
  id,
  action,
  defaultValues,
  goto,
  isLoading,
  data,
}) => {
  const nodegroupConfig = useNodegroupConfig(data?.enableDruid || ENABLE_DRUID === action, "update", defaultValues);
  const hadoopConfig = useHadoopConfig("update", defaultValues);
  const mySQLConfig = useMySQLConfig("update", defaultValues);
  const viaConfig = useVIAConfig("update", defaultValues);
  const druidConfig = useDruidConfig("update", defaultValues);
  const giteaConfig = useGiteaConfig("update", defaultValues);
  const neo4jConfig = useNeo4jConfig("update", defaultValues);

  const profileConfig =
    action === CHANGE_NODEGROUP
      ? nodegroupConfig
      : action === CHANGE_HADOOP
      ? hadoopConfig
      : action === CHANGE_MYSQL
      ? mySQLConfig
      : action === CHANGE_VIA
      ? viaConfig
      : action === CHANGE_DRUID 
      ? druidConfig
      : action === ENABLE_DRUID
      ? [nodegroupConfig.filter((item) => item.title.indexOf("Druid") != -1), druidConfig]
      : action === CHANGE_GITEA || action === ENABLE_GITEA
      ? giteaConfig
      : action === CHANGE_NEO4J || action === ENABLE_NEO4J
      ? neo4jConfig
      : [];
  return (
    <GetDefaultColumnValue
      id={id}
      profileConfig={profileConfig}
      action={action}
      goto={goto}
      isLoading={isLoading}
      defaultValues={defaultValues}
      data={data}
    ></GetDefaultColumnValue>
  );
};

export const GetDefaultColumnValue = ({
  id,
  action,
  profileConfig,
  defaultValues,
  goto,
  isLoading,
  data,
}) => {
  const [profileColumns, setProfileColumns] = useState([]);

  const [isReady, setReady] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    if (defaultValues && Object.keys(defaultValues).length == 0) return;
    if (profileConfig.length == 0) return;
    if(ENABLE_DRUID == action) {
      const columns = appendDefaultValuesToColumns(
        profileConfig,
        defaultValues,
      );
      setProfileColumns([...columns]);
    } else {
      const columns = appendDefaultValuesToColumns(
        [profileConfig],
        defaultValues,
      );
      setProfileColumns([...columns[0]]);
    }
    setReady(true);
  }, [profileConfig, defaultValues, isLoading]);

  return !isReady ? (
    "loading..."
  ) : (
    <UpdateUI
      id={id}
      action={action}
      profileColumns={profileColumns}
      goto={goto}
    ></UpdateUI>
  );
};

export const UpdateUI = ({ id, action, profileColumns, goto }) => {
  // const [profileColumns1, setProfileColumns1] = useState(
  //   JSON.parse(profileColumns),
  // );
  // const profileColumns1 = JSON.parse(profileColumns);
  // console.log(profileColumns);
  const formRef = useRef();
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();
  const navigate = useResourceNavigate();
  const invalidate = useInvalidate();

  const steps = [
    { title: 'Node Group'},
    { title: 'Druid' },
  ];

  const actionLabel =
    action === CHANGE_NODEGROUP
      ? t("instances.wizard.tab.nodegroup", "Nodegroup")
      : action === CHANGE_HADOOP
      ? t("instances.wizard.tab.hadoop", "Hadoop")
      : action === CHANGE_MYSQL
      ? t("instances.wizard.tab.mysql", "MySQL")
      : action === CHANGE_VIA
      ? t("instances.wizard.tab.via", "VIA")
      : action === CHANGE_DRUID || action === ENABLE_DRUID
      ? t("instaces.wizard.tab.druid", "Druid")
      : action === CHANGE_GITEA || action === ENABLE_GITEA
      ? t("instances.wizard.tab.gitea", "Gitea")
      : action === CHANGE_NEO4J || action === ENABLE_NEO4J
      ? t("instances.wizard.tab.neo4j", "Neo4j")
      : "";

  const { mutate: update } = useUpdate({
    resource: "instances",
    successNotification: (data, values, resource) => {
      setSubmitting(false);
      invalidate({
        resource,
        invalidates: ["list"],
      });
      navigate({
        resource: "instances",
        key: values.id,
        action: "show",
      });
      return {
        message: t(`instances.actionResult.${action}.success`, {
          id: values?.id,
        }),
        type: "success",
      };
    },
    errorNotification: (data, values, resource) => {
      setSubmitting(false);
      return {
        message: t(`instances.actionResult.${action}.failed`, {
          id: values?.id,
        }),
        type: "error",
      };
    },
  });

  return (
    <Spin spinning={submitting}>
      <UpdateWrapper
        i18nTitle={`actions.${action}`}
        title={t(`actions.${action}`)}
        desc={t(`actionsDesc.${action}`)}
        footer={<></>}
      >
        {action === ENABLE_DRUID ? 
          <BetaSchemaForm
            layoutType="StepsForm"
            formRef={formRef}
            steps={steps}
            onFinish={async (values) => {
              setSubmitting(true);
              const profile = genUpdateProfileForServer(values, action);
              update({
                id,
                values: { data: profile, action },
                // meta: { action },
              });
            }}
            columns={profileColumns}
          /> : 
          <BetaSchemaForm
            formRef={formRef}
            onFinish={async (values) => {
              setSubmitting(true);
              const profile = genUpdateProfileForServer(values, action);
              update({
                id,
                values: { data: profile, action },
                // meta: { action },
              });
            }}
            columns={profileColumns}
          />
        }
        
      </UpdateWrapper>
    </Spin>
  );
};
